import React, { useCallback, useMemo, type MutableRefObject, type FC } from 'react';
import isEqual from 'lodash/isEqual';
import type { VirtualItem, VirtualizerOptions } from '@tanstack/virtual-core';
import type { useVirtualizer } from '@tanstack/react-virtual';
import { graphql, useFragment } from 'react-relay';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { isShallowEqual } from '@atlassian/jira-platform-shallow-equal/src/index.tsx';
import {
	useFlatList,
	getShowMoreItemKey,
} from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import type { InitializeVirtualizer_groupResults$key } from '@atlassian/jira-relay/src/__generated__/InitializeVirtualizer_groupResults.graphql';
import { getFlatListKey } from '@atlassian/jira-issue-table-hierarchy/src/common/utils/get-flat-list-key/index.tsx';
import type { InitializeVirtualizer_issueResults$key } from '@atlassian/jira-relay/src/__generated__/InitializeVirtualizer_issueResults.graphql';
import { ROW_HEIGHT } from '../../../common/constants.tsx';
import { useScrollElement } from '../../../controllers/scroll-state/index.tsx';
import { useSSRSafeVirtualizer } from '../../../controllers/virtualization/index.tsx';
import {
	useVirtualizationActions,
	type VirtualItemsMap,
} from '../../../controllers/virtualization/rss-store.tsx';

type UseVirtualizerArgs = Parameters<typeof useVirtualizer<HTMLElement, Element>>[0];

export type Props = {
	/** @deprecated remove when jira_list_reduce_re_renders is cleaned up */
	rows?: number;
	// make setTableHeight required and remove totalSizeRef when cleaning up jira_list_virtualizer_table_height
	/** @deprecated remove when jira_list_reduce_re_renders is cleaned up */
	totalSizeRef?: MutableRefObject<number>;
	/** @deprecated remove when jira_list_reduce_re_renders is cleaned up */
	getItemKey?: (index: number) => number | string | bigint;
	options?: Partial<VirtualizerOptions<HTMLElement, Element>>;
	issueResults?: InitializeVirtualizer_issueResults$key | null;
	groupResults?: InitializeVirtualizer_groupResults$key | null;
};

export type PropsInner = {
	// make setTableHeight required and remove totalSizeRef when cleaning up jira_list_virtualizer_table_height
	/** @deprecated remove when jira_list_reduce_re_renders is cleaned up */
	totalSizeRef?: MutableRefObject<number>;
	options: UseVirtualizerArgs;
};
const estimateSize = () => ROW_HEIGHT + 1; // include border

const InitializeVirtualizerInner = ({
	options: virtualizerOptions,
	issueResults,
	groupResults,
}: Props) => {
	const scrollElement = useScrollElement();
	const flatList = useFlatList();
	const { setVirtualizer, setVirtualItemsMap } = useVirtualizationActions();

	const issueResultsData = useFragment<InitializeVirtualizer_issueResults$key>(
		graphql`
			fragment InitializeVirtualizer_issueResults on JiraIssueConnection
			@argumentDefinitions(
				isInitialiseVirtualiserStateFromRelayStoreEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/initialise-virtualiser-state-from-relay-store.relayprovider"
				}
			) {
				edges {
					node {
						key
						issueId @include(if: $isInitialiseVirtualiserStateFromRelayStoreEnabled)
					}
					__typename # only interested in edge presence to handle empty state
				}
			}
		`,
		issueResults,
	);

	const groupResultsData = useFragment<InitializeVirtualizer_groupResults$key>(
		graphql`
			fragment InitializeVirtualizer_groupResults on JiraSpreadsheetGroupConnection
			@argumentDefinitions(
				isPaginating: { type: "Boolean", defaultValue: false }
				isInitialiseVirtualiserStateFromRelayStoreEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/initialise-virtualiser-state-from-relay-store.relayprovider"
				}
			) {
				edges {
					node {
						id
					}
				}
				firstGroup
					@skip(if: $isPaginating)
					@include(if: $isInitialiseVirtualiserStateFromRelayStoreEnabled) {
					id
					issues(
						viewConfigInput: $viewConfigInput
						fieldSetsInput: $fieldSetsInput
						scope: $fieldSetsScope
						first: 50
						settings: $settings
					) {
						edges {
							node {
								issueId
							}
						}
						pageInfo {
							hasNextPage
						}
					}
				}
			}
		`,
		groupResults,
	);

	// initialFlatList is currently only guaranteed to keep virtualiser props stable while flatList is empty
	// If flatList is no longer empty, initialFlatList should be ignored
	const initialFlatList = useMemo(() => {
		if (!(flatList?.length <= 0 && fg('initialise_virtualiser_state_from_relay_store'))) {
			return [];
		}

		if (issueResultsData && issueResultsData.edges) {
			return issueResultsData.edges
				.map((edge) => edge?.node?.issueId)
				.filter(Boolean)
				.map((issueId) => getFlatListKey({ itemId: issueId }));
		}
		if (groupResultsData && groupResultsData.firstGroup && groupResultsData?.firstGroup?.id) {
			const firstGroupId = groupResultsData.firstGroup.id;
			return [
				getFlatListKey({
					itemId: firstGroupId,
					groupId: firstGroupId,
				}),
				...(groupResultsData.firstGroup.issues?.edges ?? [])
					.map((edge) => edge?.node?.issueId)
					.filter(Boolean)
					.map((issueId) => getFlatListKey({ itemId: issueId, groupId: firstGroupId })),
				...(groupResultsData.firstGroup.issues?.pageInfo?.hasNextPage
					? [
							getFlatListKey({
								itemId: getShowMoreItemKey(firstGroupId),
								groupId: firstGroupId,
							}),
						]
					: []),
				...(groupResultsData.edges ?? [])
					.map((edge) => edge?.node?.id)
					.filter((id) => id !== firstGroupId)
					.filter(Boolean)
					.map((id) => getFlatListKey({ itemId: id, groupId: id })),
			];
		}
		return [];
	}, [flatList?.length, groupResultsData, issueResultsData]);

	// Represents a stable list of item keys between first render (i.e. flat list is empty) and after flat list has been initialised in RSS.
	const readyFlatList: string[] =
		flatList?.length === 0 && fg('initialise_virtualiser_state_from_relay_store')
			? initialFlatList
			: flatList;

	const getItemKey = useCallback((index: number) => readyFlatList[index] ?? index, [readyFlatList]);

	// eslint-disable-next-line no-nested-ternary
	const rowsCount = fg('initialise_virtualiser_state_from_relay_store')
		? readyFlatList.length
		: flatList?.length > 0
			? flatList?.length
			: issueResultsData?.edges?.length;

	const options = useMemo(
		() => ({
			count: rowsCount ?? 0,
			getScrollElement: () => scrollElement,
			estimateSize,
			getItemKey,
			overscan: 10,
			...(fg('jira_spreadsheet_timeline_image_export') && virtualizerOptions),
		}),
		[getItemKey, rowsCount, scrollElement, virtualizerOptions],
	);

	const virtualizer = useSSRSafeVirtualizer(options);
	const items = virtualizer?.getVirtualItems();

	const prevVirtualizer = usePrevious(virtualizer);
	const prevItems = usePrevious(items);

	if (!isShallowEqual(prevVirtualizer, virtualizer)) {
		setVirtualizer(virtualizer);
	}

	if (!isEqual(prevItems, items)) {
		const virtualItemsMap = items?.reduce((acc: VirtualItemsMap, item: VirtualItem) => {
			acc[String(item.key)] = true;
			return acc;
		}, {});

		setVirtualItemsMap(virtualItemsMap);
	}

	return <InitializeVirtualizerContent getItemKey={getItemKey} rows={rowsCount ?? 0} />;
};

export const InitializeVirtualizer = (props: Props): JSX.Element | null => {
	// Skip any attempts to perform virtualization in SSR
	if (__SERVER__) {
		return null;
	}

	return <InitializeVirtualizerInner {...props} />;
};

/**
 * This component is used for testing purposes only
 */
export type InitializeVirtualizerContentProps = {
	getItemKey: (index: number) => number | string | bigint;
	rows: number;
};

export const InitializeVirtualizerContent: FC<InitializeVirtualizerContentProps> = () => {
	return null;
};
